import alertDialogService from "AlertDialogService";
import { DeferredPromise } from "DeferredPromise";
import type { ButtonResult } from "DialogService";
import drawerService from "DrawerService";
import { FormFlowError } from "FormFlowError";
import {
  cancelTaskMessageType,
  ensureSavedMessageType,
  invokeControlTransitionMessageType,
  invokeSubFormFlowMessageType,
  refreshFormMessageType,
  registerPendingActionHandlerMessageType,
  unregisterPendingActionHandlerMessageType,
  type ControlTransitionEventArgs,
  type FormFlowResultData,
  type PendingChangesHandler,
  type SubFormFlowEventArgs,
} from "FormFlowTypes";
import type { FormFlowUI, FormFooterTransition, FormTransitionResult } from "FormFlowUITypes";
import type { Alert } from "Notifications";
import "gwResizable";
import { runExclusiveAsync } from "./FormFlowUIExclusiveActions.ts";
import { handlePendingActionsAsync } from "./FormFlowUIPendingChangesHandler.ts";
import { ensureSavedAsync } from "./FormFlowUISaveChanges.ts";
import {
  isControlError,
  isRuleErrorOrWarning,
  showValidationResultsAsync,
  validateFormAsync,
} from "./FormFlowUIValidator.ts";

export function setupMessageBusSubscriptions(formFlowUI: FormFlowUI): void {
  const messageBus = formFlowUI.messageBus;

  messageBus.subscribe(refreshFormMessageType, () => refreshTaskAsync(formFlowUI));
  messageBus.subscribe(cancelTaskMessageType, () => cancelTaskAsync(formFlowUI));
  messageBus.subscribe(invokeSubFormFlowMessageType, (data) => handleSubFormFlowAsync(formFlowUI, data));
  messageBus.subscribe(invokeControlTransitionMessageType, (data) => handleControlTransitionAsync(formFlowUI, data));
  messageBus.subscribe(registerPendingActionHandlerMessageType, (data) =>
    registerPendingActionHandlerAsync(formFlowUI, data),
  );
  messageBus.subscribe(unregisterPendingActionHandlerMessageType, (data) =>
    unregisterPendingActionHandlerAsync(formFlowUI, data),
  );
  messageBus.subscribe(ensureSavedMessageType, (data) => ensureSavedAsync(formFlowUI, data.shouldRunExclusive));
}

export async function refreshTaskAsync(formFlowUI: FormFlowUI): Promise<void> {
  await runExclusiveAsync(formFlowUI, async () => {
    try {
      await formFlowUI.discardChangesAsync();
      await formFlowUI.reloadEntityAsync();
    } catch (error) {
      formFlowUI.formAwaiter.reject(error);
    }
  });
}

export async function cancelTaskAsync(formFlowUI: FormFlowUI): Promise<void> {
  return await runExclusiveAsync(formFlowUI, async () => {
    const isHandled = await handlePendingActionsAsync(formFlowUI);
    let shouldAbort: ButtonResult;
    if (!isHandled) {
      shouldAbort = false;
    } else if (!(formFlowUI.hasChanges() || drawerService.hasChanges())) {
      shouldAbort = true;
    } else {
      shouldAbort = await alertDialogService.warnStayLeaveUnsavedChangesAsync();
    }

    if (shouldAbort) {
      formFlowUI.abort();
    }
  });
}

async function handleSubFormFlowAsync(
  formFlowUI: FormFlowUI,
  eventArgs: SubFormFlowEventArgs,
): Promise<FormFlowResultData | void> {
  const invokerAsync = formFlowUI.handleSubFormFlowAsync;
  if (invokerAsync) {
    await runExclusiveAsync(formFlowUI, async () => {
      await invokeTransitionAsync(formFlowUI, () => invokerAsync(eventArgs), eventArgs.SuspendValidation);
    });
    return {
      formFlowPK: eventArgs.formFlowPK,
      entityPK: eventArgs.entityPK,
      entityPKs: eventArgs.entityPKs,
    };
  } else {
    formFlowUI.formAwaiter.reject(
      new FormFlowError(
        `Expected sub form-flow handler for form-flow with formFlowPK:  ${eventArgs.formFlowPK}, but did not exist.`,
      ),
    );
  }
}

async function handleControlTransitionAsync(
  formFlowUI: FormFlowUI,
  eventArgs: ControlTransitionEventArgs,
): Promise<void> {
  await runExclusiveAsync(formFlowUI, async () => {
    eventArgs.isHandled = true;

    const transition = formFlowUI.getControlTransition(eventArgs.controlId);
    if (transition) {
      await invokeTransitionAsync(
        formFlowUI,
        () => transition.onTransitionAsync(eventArgs),
        transition.SuspendValidation,
      );
    } else {
      formFlowUI.formAwaiter.reject(
        new FormFlowError(`Expected transition with ControlId ${eventArgs.controlId}, but did not exist.`),
      );
    }
  });
}

export async function handleScanTransitionAsync(formFlowUI: FormFlowUI): Promise<void> {
  const transitions = formFlowUI.buttonConfig?.footerTransitions?.();
  if (transitions?.length) {
    await handleFooterTransitionAsync(formFlowUI, transitions[0]);
  }
}

export async function handleFooterTransitionAsync(
  formFlowUI: FormFlowUI,
  transition: FormFooterTransition,
): Promise<void> {
  await runExclusiveAsync(formFlowUI, () =>
    invokeTransitionAsync(formFlowUI, () => transition.onTransitionAsync(), transition.SuspendValidation),
  );
}

async function invokeTransitionAsync(
  formFlowUI: FormFlowUI,
  invoker: () => Promise<FormTransitionResult | undefined>,
  suspendValidation?: boolean,
): Promise<void> {
  const isValid = await validateFormAsync(formFlowUI, suspendValidation);
  if (!isValid) {
    await showValidationResultsAsync(formFlowUI, shouldPreventTransition);
  }

  let shouldTransition;
  if (isValid && !formFlowUI.isAborted) {
    shouldTransition = await handlePendingActionsAsync(formFlowUI);

    if (shouldTransition && !formFlowUI.isAborted) {
      //This additional layer lets a transition deferred complete early when a form is shown, so that controls on a reused form can stop waiting for the transition to finish.
      const thisShowFormDeferred = formFlowUI.formAwaiter;
      const thisTransitionDeferred = (formFlowUI.transitionAwaiter = new DeferredPromise<void>());

      (async (): Promise<void> => {
        try {
          const result = await invoker();
          const { remainOpen } = result ?? {};
          formFlowUI.log?.(`transition resolved with { remainOpen: ${remainOpen} }`);
          if (!remainOpen) {
            thisShowFormDeferred?.resolve();
          }
          thisTransitionDeferred.resolve();
        } catch (error) {
          thisShowFormDeferred?.reject(error);
          thisTransitionDeferred.resolve();
        }
      })(); // do not await
      return await thisTransitionDeferred.promise;
    }
  }
}

function shouldPreventTransition(alert: Alert): boolean {
  return isControlError(alert) || isRuleErrorOrWarning(alert);
}

async function registerPendingActionHandlerAsync(
  formFlowUI: FormFlowUI,
  handler: PendingChangesHandler,
): Promise<void> {
  if (handler && typeof handler.handlePendingActionsAsync === "function") {
    await formFlowUI.pendingActionHandlers.add(handler);
  } else {
    throw new Error("Pending action handler should implement handlePendingActionsAsync function.");
  }
}

async function unregisterPendingActionHandlerAsync(
  formFlowUI: FormFlowUI,
  handler: PendingChangesHandler,
): Promise<void> {
  if (handler) {
    await formFlowUI.pendingActionHandlers.delete(handler);
  } else {
    throw new Error("Pending action handler should be defined.");
  }
}
